// ============================================================================
// SHORTAGE BOARD TYPES
// ============================================================================
//
// Mirrors crs.Core/DTOs/InventoryModule/ShortageBoardDtos.cs.
// Reference: crs-doc/CRS-Inventory-Shortage-and-Oversell-Design.md §9

/** Actions a plan line can propose. Must match the DB CHECK constraint exactly. */
export const SHORTAGE_ACTIONS = [
  'UPGRADE',
  'RELOCATE',
  'WALK',
  'ABSORB',
  'CONSOLIDATE',
  'RELEASE_HOLD',
  'COLLECT_ADVANCE',
  'OTHER',
] as const;

export type ShortageAction = typeof SHORTAGE_ACTIONS[number];

export const SHORTAGE_ACTION_LABELS: Record<ShortageAction, string> = {
  UPGRADE: 'Upgrade',
  RELOCATE: 'Relocate to another property',
  WALK: 'Walk',
  ABSORB: 'Absorb (carry the shortage)',
  CONSOLIDATE: 'Consolidate rooms',
  RELEASE_HOLD: 'Let the hold lapse',
  COLLECT_ADVANCE: 'Chase the advance',
  OTHER: 'Other',
};

/** Levers a stay's own facts support. Never a prediction — always a stored fact. */
export type ShortageLever =
  | 'EXPIRING_HOLD'
  | 'ADVANCE_OVERDUE'
  | 'NOT_YET_FIRM'
  | 'LOCKED'
  | 'CATEGORY_SHIFT'
  | 'SISTER_PROPERTY'
  | 'CONSOLIDATION';

export const SHORTAGE_LEVER_LABELS: Record<ShortageLever, string> = {
  EXPIRING_HOLD: 'Hold expiring',
  ADVANCE_OVERDUE: 'Advance overdue',
  NOT_YET_FIRM: 'Not yet firm',
  LOCKED: 'In house',
  CATEGORY_SHIFT: 'Category free',
  SISTER_PROPERTY: 'Sister property free',
  CONSOLIDATION: 'Could consolidate',
};

/**
 * A party holding more rooms than its size requires.
 *
 * Identified only, never auto-applied: consolidating means asking guests who
 * booked separate rooms to share, and no master data says whether that is
 * acceptable. The board shows the arithmetic; a person makes the call.
 */
export interface ConsolidationOption {
  partyAdults: number;
  partyChildren: number;
  roomsHeld: number;
  /** Fewest rooms the party fits into without breaching a category limit. */
  minRoomsNeeded: number;
  /** Rooms this would release. Always > 0 when present. */
  roomsFreed: number;
  maxOccupancy: number;
}

/** Where a category sits relative to the guest's, by the property's own ordering. */
export type CategoryShiftDirection = 'UPGRADE' | 'DOWNGRADE' | 'LATERAL';

/** Another category at the same property that could take the whole stay. */
export interface CategoryShiftOption {
  roomCategoryId: string;
  categoryCode: string;
  categoryName: string;
  /** Free rooms on the tightest night of the stay — the binding constraint. */
  minAvailableRooms: number;
  direction: CategoryShiftDirection;
}

/** Another property in the group that could host the whole stay. */
export interface SisterPropertyOption {
  propertyId: string;
  propertyCode: string;
  propertyName: string;
  roomCategoryId: string;
  categoryName: string;
  minAvailableRooms: number;
}

export type ShortagePlanStatusCode = 'PLAN_PLANNED' | 'PLAN_EXECUTED' | 'PLAN_ABANDONED';

/** A stay exposed to a shortage, with the facts that make it actionable. */
export interface ShortageAffectedStay {
  bookingId: string;
  bookingNumber: string;
  guestName?: string | null;
  bookingStatusCode: string;
  bookingStatus?: string | null;
  checkInDate: string;
  checkOutDate: string;
  /** Rooms this booking holds in the short category on this night. */
  roomsInCategory: number;
  numAdults: number;
  numChildren: number;
  expiresAt?: string | null;
  advanceRequired: number;
  advanceReceived: number;
  advanceDueDate?: string | null;
  /** Empty means the stay is exposed but offers nothing the system can point at. */
  levers: ShortageLever[];
  /** Categories at this property that fit the whole stay. Upgrade-first. */
  categoryShiftOptions: CategoryShiftOption[];
  /** Other properties in the group that fit the whole stay. */
  sisterPropertyOptions: SisterPropertyOption[];
  /**
   * Present only when the party would genuinely fit into fewer rooms. Null means
   * either that it would not, or that CRS does not know the party size — both are
   * "do not suggest this", and neither is a guess.
   */
  consolidation?: ConsolidationOption | null;
}

/** One worksheet line: what will be done about one booking on one night. */
export interface ShortagePlanDetail {
  id: string;
  shortagePlanId: string;
  roomCategoryId: string;
  shortageDate: string;
  affectedBookingId: string;
  affectedBookingNumber?: string | null;
  affectedGuestName?: string | null;
  proposedAction: ShortageAction;
  detailStatusCode?: ShortagePlanStatusCode | null;
  detailStatus?: string | null;
  /** What actually happened — often not what was proposed. */
  executedAction?: ShortageAction | null;
  executedRef?: string | null;
  executedBy?: string | null;
  executedAt?: string | null;
  notes?: string | null;
  rowVersion: number;
}

/** Header of one planning exercise. */
export interface ShortagePlan {
  id: string;
  propertyId: string;
  roomCategoryId: string;
  categoryName?: string | null;
  /** Inclusive — plans have no departure day, unlike stay dates. */
  dateFrom: string;
  /** Inclusive. */
  dateTo: string;
  ownerUserId?: string | null;
  ownerUserName?: string | null;
  planStatusCode?: ShortagePlanStatusCode | null;
  planStatus?: string | null;
  notes?: string | null;
  rowVersion: number;
  details: ShortagePlanDetail[];
}

/**
 * How far a night's shortage reaches. Both are real shortages in the category;
 * the difference is what can be done about it.
 */
export type ShortageScope =
  /** Free rooms elsewhere in the property cover the night's total oversell. */
  | 'CATEGORY_ONLY'
  /** The building itself is short — someone has to be relocated or walked. */
  | 'PROPERTY_WIDE';

/** One night on which a category is committed beyond its inventory. */
export interface ShortageNight {
  propertyId: string;
  roomCategoryId: string;
  categoryCode: string;
  categoryName: string;
  night: string;
  totalRooms: number;
  bookedRooms: number;
  /** Rooms still sellable in this category tonight. Always 0 on a board row. */
  availableRooms: number;
  /** Rooms owed beyond inventory. Always > 0 for a row on this board. */
  oversoldRooms: number;
  affectedStays: ShortageAffectedStay[];
  planDetails: ShortagePlanDetail[];
  /**
   * Rooms of excess nobody has recorded a decision for yet. An ABSORB line
   * counts as covered — absorbing IS a decision, even though the shortage
   * stays live.
   */
  /** Exposed bookings on this night with no decision recorded yet. */
  undecidedBookings: number;

  // ── Property-wide context for this night ────────────────────────────────
  /** Sellable rooms across every active category at this property. */
  propertyTotalRooms: number;
  propertyCommittedRooms: number;
  /**
   * Free rooms across every category. The short category contributes zero
   * (availability is clamped), so this is effectively "free elsewhere".
   */
  propertyAvailableRooms: number;
  /** Total oversell across ALL categories this night, not just this one. */
  propertyOversoldRooms: number;
  /**
   * Rooms the PROPERTY is short once free rooms elsewhere are counted against
   * the night's total oversell. 0 = absorbable in-house.
   */
  propertyShortfallRooms: number;
  scope: ShortageScope;
}

export interface ShortageBoardQuery {
  propertyId: string;
  dateFrom?: string;
  dateTo?: string;
  roomCategoryId?: string;
}

export interface UpsertShortagePlanRequest {
  propertyId: string;
  roomCategoryId: string;
  dateFrom: string;
  dateTo: string;
  ownerUserId?: string | null;
  notes?: string | null;
}

export interface UpsertShortagePlanDetailRequest {
  shortagePlanId: string;
  shortageDate: string;
  affectedBookingId: string;
  /**
   * Optional. Omitted, the server records the line as OTHER and `notes` carries
   * the decision in the desk's own words. Kept for a future remark master —
   * nothing in CRS branches on the value today.
   */
  proposedAction?: ShortageAction;
  notes?: string | null;
}

export interface ExecuteShortagePlanDetailRequest {
  /** Optional — the proposed action stands in when the desk did what it planned. */
  executedAction?: ShortageAction | null;
  /** Free text identifying the operation that was actually run. */
  executedRef?: string | null;
  notes?: string | null;
  rowVersion: number;
}
